// components/gamification/StreakModal.js
"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useFuncion } from "../../context/FuncionContext";
import { ECUADOR_TZ, getEcuadorYMD } from "../../utils/ecuadorTime";

/* ====== Frases y selector sin repetición ====== */
export const STREAK_PHRASES = [
  "Paso a paso, como un bucle bien hecho.",
  "Te luciste hoy: más lógica, menos bugs.",
  "Otro día, otra función dominada.",
  "Suma de hábitos > suma de errores.",
  "Hoy compilaste tu constancia.",
  "Debuggear también es avanzar.",
  "Hoy tu mente hizo push al main.",
  "Código claro, ideas claras.",
  "Una prueba más verde en tu día.",
  "Refactoriza tus dudas, ejecuta tus ideas.",
  "Tu foco = tu framework más potente.",
  "Cada día nuevo, una nueva build.",
  "Aprender es el mejor commit.",
  "Tus ganas son el mejor IDE.",
  "Pequeños pasos, grandes versiones.",
  "Hoy corriste el script de la disciplina.",
  "Tu lógica hoy no tiene nulls.",
  "Eres constante como una promesa resuelta.",
  "Tu disciplina no tiene memory leaks.",
  "Menos excusas, más ejecución.",
  "Cada día te debugeas mejor.",
  "Tu roadmap cada vez más limpio.",
  "Hoy optimizaste tu propio runtime.",
  "Tu actitud: O(1) ante las dudas.",
  "Hoy tu mente pasó todos los tests.",
  "Compilas ideas, ejecutas resultados.",
  "Tu progreso no es async: se nota.",
  "Documentas tu futuro con cada día.",
  "Subiste versión de ti mismo.",
  "Tu hábito no hace rollback.",
  "Otra iteración exitosa.",
  "Tu constancia es tipada: ¡fuerte!",
  "Sin prisa, sin pausa, como una cola.",
  "Hoy hiciste merge con tus metas.",
  "Tu stack: disciplina, curiosidad, práctica.",
  "Hoy no hubo excepción a tu constancia.",
  "Tu aprendizaje: idempotente y sólido.",
  "Cada día: menos deuda técnica personal.",
  "Tu foco no lanza warnings.",
  "Una rama más hacia el objetivo.",
  "Hoy el compilador te guiñó el ojo.",
  "Sigue iterando, dev.",
  "Cada día un PR aprobado por ti.",
  "El hábito es tu mejor patrón.",
  "Build tras build, te pones pro.",
  "Hoy hiciste hot-reload de motivación.",
  "Tus metas con tipado estricto.",
  "La constancia es tu CI/CD.",
  "Tu mente hoy corre en modo release.",
  "Hoy hiciste cache de confianza.",
  "Tu esfuerzo: 100% coverage emocional.",
  "Un día más, un bug menos.",
  "Hoy hiciste profiling de tus hábitos.",
  "Tu plan no tiene race conditions.",
  "Refactor de dudas → claridad.",
  "Cada día: un microservicio de progreso.",
  "Tus límites hoy son mocks.",
  "Aprender es tu dependencia favorita.",
  "Hoy hiciste deploy de valentía.",
  "Tu motivación no se quiebra en producción.",
  "Todo gran sistema empezó simple.",
  "Tu disciplina hace scale.",
  "Hoy hiciste ping a tus sueños.",
  "Tu energía tiene baja latencia.",
  "Eres resiliente como un cluster.",
  "Tus ganas tienen alta disponibilidad.",
  "Tu código interno, cada vez más limpio.",
  "Hoy hiciste health-check de tu foco.",
  "Tu esfuerzo no entra en timeout.",
  "Un día más, un skill más.",
  "Hoy evitaste el anti-patrón del “después”.",
  "Tu constancia es transaccional.",
  "Otra historia cerrada en tu sprint.",
  "Tu autoestima: status 200 OK.",
  "Deploy continuo de disciplina.",
  "Hoy tu actitud no arrojó 500.",
  "Tu aprendizaje hace autoscaling.",
  "Menos “mañana”, más “run”.",
  "Cada día reduces la complejidad.",
  "Tu constancia tiene logs de éxito.",
  "Hoy liberaste una versión mejor de ti.",
  "Tu mente: zero-downtime learning.",
  "Un test diario: presente.",
  "Hoy hiciste GC de excusas.",
  "Tu progreso no tiene locks.",
  "Nueva feature: confianza.",
  "Tu roadmap personal está on track.",
  "Hoy hiciste lint a tus miedos.",
  "Tu hábito compila en cualquier entorno.",
  "Otra review superada por ti.",
  "Hoy escalaste un nivel más.",
  "Tu motivación está cacheada.",
  "Tu voluntad tiene SLA alto.",
  "Cada día reduces latencia de dudas.",
  "Hoy performaste excelente.",
  "Tu constancia no hace memory thrash.",
  "Reintentas hasta que pasa.",
  "Tus sueños tienen uptime total.",
  "Hoy hiciste fork del miedo y lo borraste.",
  "Otro commit de crecimiento.",
  "Tu build: estable y consistente.",
  "Hoy firmaste un contrato con tu meta.",
  "Tu práctica deja métricas claras.",
  "Hoy hiciste benchmark de tu mejor versión.",
  "Sigue, dev: tu release grande viene.",
];

/* ====== Identificador único por estudiante ====== */
const decodeJwtPayload = (token) => {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const getUserKeyFromStorage = () => {
  try {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("accessToken");

    const payload = decodeJwtPayload(token);
    return (
      payload?.sub ||
      payload?.id ||
      payload?.user_id ||
      payload?.email ||
      payload?.codigo_institucional ||
      "anon"
    );
  } catch {
    return "anon";
  }
};

const getStreakInfoKey = (userKey) =>
  `streak_info_seen:v2:${String(userKey || "anon").replace(/[^a-z0-9_-]/gi, "").slice(0, 48)}`;

const usedIndexes = new Set();
export const getNextStreakPhrase = () => {
  if (usedIndexes.size >= STREAK_PHRASES.length) usedIndexes.clear();
  let idx = Math.floor(Math.random() * STREAK_PHRASES.length);
  while (usedIndexes.has(idx)) idx = Math.floor(Math.random() * STREAK_PHRASES.length);
  usedIndexes.add(idx);
  return STREAK_PHRASES[idx];
};

/* ====== Recompensas por día (ciclo 7) ====== */
const getRewardForDay = (day) => {
  const slot = ((day - 1) % 7) + 1; // 1..7

  if (slot === 7) {
    return { type: "comodin", amount: 1, icon: "🃏", text: "COMODÍN", pill: "COMODÍN", tier: "max" };
  }
  if (slot === 4) {
    return { type: "coins", amount: 10, icon: "🪙", text: "10", pill: "+10", tier: "big" };
  }
  return { type: "coins", amount: 5, icon: "🪙", text: "5", pill: "+5", tier: "base" };
};

/* ===== Overlay toast encima de todo (NO cierra el modal) ===== */
const GlobalToast = ({
  open,
  title = "¡Recompensa!",
  subtitle,
  icon = "🪙",
  onDone,
  duration = 6000,
  variant = "default",
}) => {
  const [mounted, setMounted] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // ✅ si el padre cierra el toast, apágalo inmediatamente
    if (!open) {
      setMounted(false);
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    setMounted(true);
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setMounted(false);
      onDone?.();
    }, duration);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open, duration, onDone]);


  if (!mounted) return null;

  const isStore = variant === "store";
  const bgGradient = isStore
    ? "linear-gradient(135deg, #0f4c3a 0%, #1e3a5f 50%, #2d1b4e 100%)"
    : "#0b1b36";
  const glowColor = isStore ? "16,185,129" : "59,130,246";

  return ReactDOM.createPortal(
    <div className="st-global-toast-wrap" aria-live="polite">
      <div className="st-global-toast" style={{ background: bgGradient }}>
        <div className="coin-icon" aria-hidden>
          <span>{icon}</span>
        </div>
        <div className="txt">
          <div className="title">{title}</div>
          {subtitle ? <div className="sub">{subtitle}</div> : null}
        </div>
      </div>
      <style>{`
        .st-global-toast-wrap{ position:fixed; inset:0; display:grid; place-items:center; pointer-events:none; z-index: 9999; }
        .st-global-toast{
          display:flex; align-items:center; gap:14px; padding:16px 18px; border-radius:18px; color:#e6edf6;
          border:1px solid rgba(255,255,255,.12);
          box-shadow: 0 24px 70px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.08);
          transform: translateY(-8px) scale(.96);
          animation: toastIn .35s ease forwards, glow${isStore ? "Store" : ""} 1.4s ease-in-out infinite;
        }
        .st-global-toast .title{ font-weight:900; font-size:22px; letter-spacing:.3px; }
        .st-global-toast .sub{ opacity:.95; color:#cfe0ff; margin-top:2px; }
        .coin-icon{ width:52px; height:52px; border-radius:14px; display:grid; place-items:center;
          background: linear-gradient(135deg,#ffd166,#ff9f1c); box-shadow: 0 12px 24px rgba(255,159,28,.35);
          transform: scale(.8); animation: coinPop .4s ease .1s forwards;
        }
        .coin-icon span{ font-size:28px; }
        @keyframes toastIn{ to{ transform: translateY(0) scale(1); } }
        @keyframes coinPop{ 60%{ transform: scale(1.08) rotate(-6deg); } 100%{ transform: scale(1) rotate(0deg); } }
        @keyframes glow{
          0%{ box-shadow: 0 24px 70px rgba(0,0,0,.45), 0 0 0 0 rgba(59,130,246,.25); }
          70%{ box-shadow: 0 24px 70px rgba(0,0,0,.45), 0 0 0 18px rgba(59,130,246,0); }
          100%{ box-shadow: 0 24px 70px rgba(0,0,0,.45), 0 0 0 0 rgba(59,130,246,0); }
        }
        @keyframes glowStore{
          0%{ box-shadow: 0 24px 70px rgba(0,0,0,.45), 0 0 0 0 rgba(${glowColor},.25); }
          70%{ box-shadow: 0 24px 70px rgba(0,0,0,.45), 0 0 0 18px rgba(${glowColor},0); }
          100%{ box-shadow: 0 24px 70px rgba(0,0,0,.45), 0 0 0 0 rgba(${glowColor},0); }
        }
      `}</style>
    </div>,
    document.body
  );
};

/* ===== Tip “Regresa mañana” anclado ===== */
const NextDayTip = ({ anchorEl, open, onClose }) => {
  if (!open || !anchorEl) return null;
  const rect = anchorEl.getBoundingClientRect();
  const top = rect.top - 16;
  const left = rect.left + rect.width / 2;

  return ReactDOM.createPortal(
    <div className="st-nextday-tip" style={{ top, left }}>
      <div className="content">
        <strong>Regresa mañana</strong>
        <span>para conseguir más recompensas</span>
        <button className="ok" onClick={onClose}>
          De acuerdo
        </button>
      </div>
      <div className="arrow" />
      <style>{`
        .st-nextday-tip{ position:fixed; transform: translate(-50%,-100%); z-index: 9998; pointer-events:auto; }
        .st-nextday-tip .content{
          display:flex; flex-direction:column; gap:6px; padding:12px 14px; border-radius:12px;
          background:#0b1b36; color:#e6edf6; border:1px solid rgba(255,255,255,.12);
          box-shadow: 0 18px 40px rgba(0,0,0,.35); min-width: 220px; align-items:center; text-align:center; animation: tipIn .18s ease;
        }
        .st-nextday-tip .ok{ margin-top:6px; height:32px; padding:0 12px; border-radius:8px; border:1px solid #c7d2fe; background:#eef2ff; color:#1e3a8a; font-weight:800; cursor:pointer; }
        .st-nextday-tip .arrow{
          width:12px; height:12px; transform: translate(-50%,4px) rotate(45deg); background:#0b1b36;
          border-left:1px solid rgba(255,255,255,.12); border-top:1px solid rgba(255,255,255,.12);
          position:absolute; left:50%; bottom: -6px;
        }
        @keyframes tipIn{ from{ opacity:0; transform: translate(-50%,-110%); } to{ opacity:1; transform: translate(-50%,-100%);} }
      `}</style>
    </div>,
    document.body
  );
};

/* ===== Modal informativo (primera vez) ===== */
const StreakInfoModal = ({ open, onClose }) => {
  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="st-info-overlay" role="dialog" aria-modal="true">
      <div className="st-info-card">
        <div className="st-info-title">🎁 Recompensas de racha</div>

        <ul className="st-info-list">
          <li>
            <span className="ic">🪙</span> Días 1, 2, 3, 5 y 6: <strong>+5 UTMCoins</strong>
          </li>
          <li>
            <span className="ic">✨</span> Día 4: <strong>+10 UTMCoins</strong> (premio grande)
          </li>
          <li>
            <span className="ic">🃏</span> Día 7: <strong>+1 comodín</strong> (premio máximo)
          </li>
        </ul>

        <div className="st-info-note">
          El <strong>comodín</strong> se agregará al <strong>último componente desbloqueado</strong>.
        </div>

        <button className="st-info-ok" onClick={onClose}>
          Entendido
        </button>
      </div>

      <style>{`
        .st-info-overlay{
          position:fixed; inset:0;
          background: rgba(2,6,23,.55);
          display:grid; place-items:center;
          z-index: 7000;
          padding:14px;
        }
        .st-info-card{
          width:min(520px, 100%);
          background:#fff;
          border:1px solid #e5e7eb;
          border-radius:18px;
          box-shadow: 0 30px 80px rgba(0,0,0,.35);
          padding:16px 16px 14px;
        }
        .st-info-title{
          font-weight:900; font-size:18px; color:#0b3b7a;
          margin-bottom:10px;
        }
        .st-info-list{
          margin:0; padding-left:18px;
          display:flex; flex-direction:column; gap:8px;
          color:#111827;
        }
        .st-info-list .ic{ margin-right:8px; }
        .st-info-note{
          margin-top:12px;
          background:#f8fafc;
          border:1px solid #e2e8f0;
          border-radius:12px;
          padding:10px 12px;
          color:#0f172a;
          font-weight:700;
        }
        .st-info-ok{
          margin-top:12px;
          width:100%;
          height:40px;
          border:none;
          border-radius:12px;
          background:#0b3b7a;
          color:#fff;
          font-weight:900;
          cursor:pointer;
        }
      `}</style>
    </div>,
    document.body
  );
};

/* ===== Normalización de racha ===== */
const normalizeStreak = (rawIn) => {
  const r = rawIn?.racha ?? rawIn ?? {};
  const current = Number(r.current ?? r.racha_actual_dias ?? 0);
  const best = Number(r.best ?? r.mejor_racha_dias ?? 0);
  const pendingClaims = Number(r.pending_claims ?? r.pendingClaims ?? 0); // ✅ NUEVO

  const lastClaimYMD =
    r.lastClaimYMD ??
    r.last_claim_ymd ??
    (r.fecha_ultima_conexion
      ? new Date(r.fecha_ultima_conexion).toISOString().slice(0, 10)
      : null);

const claimedToday = r.claimedToday ?? lastClaimYMD === getEcuadorYMD();
const didTaskToday = !!(r.didTaskToday ?? r.did_task_today);
const didLevelTestToday = !!(r.didLevelTestToday ?? r.did_level_test_today);
const rawEligible = !!(r.eligibleToday ?? r.eligible_today);
const eligibleToday = rawEligible || didTaskToday || didLevelTestToday;

  return {
    current,
    best,
    lastClaimYMD,
    claimedToday,
    eligibleToday,
    didTaskToday,
    didLevelTestToday,
    pendingClaims,  // ✅ NUEVO
  };
};

/* ====== Modal principal ====== */
const StreakModal = ({
  open,
  onClose,
  streakDays,
  claimedMap,
  lastClaimYMD,
  onClaim,
  totalDays = null,
  variant = "default",
}) => {
  const { studentStreak, getStudentStreak, claimStudentStreak, initializeStudentData, refreshUTMCoins } = useFuncion();

  const [claiming, setClaiming] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastCfg, setToastCfg] = useState({ title: "", subtitle: "", icon: "🪙" });
  const [tip, setTip] = useState({ open: false, anchor: null });
  const [localClaimed, setLocalClaimed] = useState({});
  const [freshStreak, setFreshStreak] = useState(null); // ✅ datos frescos del servidor
  const streakCacheRef = useRef({ at: 0, data: null });
  const STREAK_CACHE_MS = 8000;

  useEffect(() => {
    if (!open) return;
    setToastOpen(false);
    setTip({ open: false, anchor: null });
    setClaiming(false);

    try {
      if (typeof window !== "undefined") {
        const userKey = getUserKeyFromStorage();
        const key = getStreakInfoKey(userKey);
        const seen = window.localStorage.getItem(key);
        if (!seen) setInfoOpen(true);
      }
    } catch { }

    initializeStudentData?.();

    // ✅ Siempre re-fetch al abrir para tener eligibleToday fresco
    getStudentStreak?.()
      .then((res) => {
        console.log("🔥 STREAK RAW:", JSON.stringify(res, null, 2));
        streakCacheRef.current = { at: Date.now(), data: res ?? true };
        setFreshStreak(res ?? null); // ✅ guarda respuesta fresca
      })
      .catch(() => { });

  }, [open, initializeStudentData, getStudentStreak]);

  // ✅ freshStreak siempre tiene prioridad absoluta sobre el contexto global
const ctx = useMemo(() => normalizeStreak(freshStreak ?? studentStreak), [freshStreak]); // eslint-disable-line

  const numericDaysRaw = typeof streakDays === "number" ? streakDays : ctx.current ?? 0;
  const numericDays = Number.isFinite(numericDaysRaw) ? numericDaysRaw : 0;

  const lastYMD = lastClaimYMD ?? ctx.lastClaimYMD ?? null;
  const todayYMD = getEcuadorYMD();

  const hasClaimedToday = !!(ctx.claimedToday) || lastYMD === todayYMD;
  const eligibleToday = !!ctx.eligibleToday;
  const canClaimToday = (!hasClaimedToday && eligibleToday) || ctx.pendingClaims > 0;

  const nextDayNumber = numericDays + 1;

  const baseClaimedMap = useMemo(() => {
    if (claimedMap && Object.keys(claimedMap).length > 0) return claimedMap;
    const m = {};
    for (let i = 1; i <= numericDays; i++) m[i] = true;
    return m;
  }, [claimedMap, numericDays]);

  const mergedClaimedMap = useMemo(() => ({ ...baseClaimedMap, ...localClaimed }), [baseClaimedMap, localClaimed]);

  const PER_PAGE = 15;
  const pages = 999999; // ✅ infinito visual (pager)

  useEffect(() => {
    const idx = Math.floor((nextDayNumber - 1) / PER_PAGE);
    setPage(Math.min(Math.max(idx, 0), pages - 1));
  }, [nextDayNumber, pages, open]);

  const allDays = useMemo(() => {
    const start = page * PER_PAGE + 1;
    return Array.from({ length: PER_PAGE }, (_, i) => start + i);
  }, [page]);


  const closeInfo = () => {
    try {
      // ✅ NUEVA LÓGICA: por estudiante
      const userKey = getUserKeyFromStorage();
      const key = getStreakInfoKey(userKey);
      window.localStorage.setItem(key, "1");
    } catch { }
    setInfoOpen(false);
  };

  const showNeedActivityToast = () => {
    setToastCfg({
      title: "Aún no puedes reclamar",
      subtitle: "Completa una tarea o un test de nivel hoy y vuelve aquí.",
      icon: "🧩",
    });
    setToastOpen(true);
  };

  const tryClaim = async (day) => {
    if (claiming) return;
    if (day !== nextDayNumber) return;

    // ✅ NUEVO: bloquear si no hizo actividad hoy
    if (!eligibleToday) {
      showNeedActivityToast();
      return;
    }

    if (hasClaimedToday) return;

    setClaiming(true);
    let apiRes = null;

    try {
      if (typeof onClaim === "function") {
        apiRes = await onClaim(day, { ymd: todayYMD, timezone: ECUADOR_TZ });
      } else if (typeof claimStudentStreak === "function") {
        apiRes = await claimStudentStreak();
      }

      await refreshUTMCoins?.();
// ✅ re-fetch fresco y guardamos en estado local, no dejamos que el contexto pise
const updatedStreak = await getStudentStreak?.().catch(() => null);
if (updatedStreak) setFreshStreak(updatedStreak);
    } catch (err) {
      // Si backend responde STREAK_NOT_ELIGIBLE (por seguridad), mostramos el mismo mensaje
      if (err?.code === "STREAK_NOT_ELIGIBLE") {
        showNeedActivityToast();
      } else {
        console.error("streak claim error", err);
      }
    } finally {
      setClaiming(false);
    }

    if (!apiRes) return;

    const claimedDayFromServer = Number(apiRes.streakDayClaimed ?? day ?? 0);
    setLocalClaimed((prev) => ({ ...prev, [claimedDayFromServer]: true }));

    const pendingCount = Number(apiRes?.pendingClaimed ?? 1);
    const phrase = getNextStreakPhrase();
    const gotComodin =
      apiRes?.comodin_awarded === true ||
      Number(apiRes?.comodin_amount ?? 0) > 0 ||
      apiRes?.awarded_type === "comodin";

    const coinsAmt = Number(apiRes?.awarded ?? apiRes?.awardedCoins ?? apiRes?.utmcoins_awarded ?? 0);
    const gotCoins = apiRes?.awarded_type === "utmcoins" || coinsAmt > 0;

    setToastCfg({
      title: gotComodin
        ? "¡+1 Comodín!"
        : gotCoins
          ? pendingCount > 1
            ? `¡+${coinsAmt} UTMCoins! (${pendingCount} días)`
            : `¡+${coinsAmt} UTMCoins!`
          : "¡Reclamado!",
      subtitle: phrase,
      icon: gotComodin ? "🃏" : "🪙",
    });

    setToastOpen(true);

    const next = claimedDayFromServer + 1;
    setTimeout(() => {
      const anchor = document.querySelector(`[data-streak-day="${next}"]`);
      if (anchor) setTip({ open: true, anchor });
    }, 6200);
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="streak-modal-overlay" role="dialog" aria-modal="true">
      <StreakInfoModal open={infoOpen} onClose={closeInfo} />

      <div className="streak-modal">
        <header className="sm-head">
          <div className="title">
            <span className="bolt">⚡</span> Tu racha diaria
          </div>
          <button
            className="close"
            onClick={() => {
              // ✅ Reset al cerrar para que al reabrir no salga “Aún no puedes reclamar”
              setToastOpen(false);
              setTip({ open: false, anchor: null });
              setClaiming(false);
              onClose?.();
            }}
            aria-label="Cerrar"
          >
            ✕
          </button>

        </header>

        <div className="sm-sub">
          <span>
            Racha: <strong>{numericDays}</strong> día(s)
          </span>
          <span className="dot">•</span>

          {/* ✅ NUEVO: indicador discreto */}
          {!hasClaimedToday && !eligibleToday ? (
            <span className="need-act-pill" title="Completa una tarea o test de nivel hoy para habilitar el reclamo">
              Completa una tarea o test hoy
            </span>
          ) : null}

          <div className="pager">
            <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              ‹
            </button>
            <span>
              {" "}
              Días {page * PER_PAGE + 1}–{page * PER_PAGE + PER_PAGE}
            </span>
            <button disabled={page >= pages - 1} onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}>
              ›
            </button>
          </div>
        </div>

        <div className="grid">
          {allDays.map((absolute) => {
            const reward = getRewardForDay(absolute);
            const rewardClass = reward.tier === "max" ? "reward-max" : reward.tier === "big" ? "reward-big" : "reward-base";

            const claimed = !!mergedClaimedMap[absolute];

            // ✅ NUEVO: claimable SOLO si eligibleToday
            const isClaimableNow = canClaimToday && absolute === nextDayNumber;

            // ✅ NUEVO: “día de hoy pero falta actividad”
            const needsActivity = !hasClaimedToday && !eligibleToday && absolute === nextDayNumber;

            const isTomorrow = hasClaimedToday && absolute === nextDayNumber;
            const locked = !claimed && !isClaimableNow && !needsActivity && !isTomorrow && absolute > nextDayNumber;

            const status = claimed
              ? "claimed"
              : isClaimableNow
                ? "pending"
                : needsActivity
                  ? "need-activity"
                  : isTomorrow
                    ? "pending"
                    : locked
                      ? "locked"
                      : "passed";

            const label = claimed
              ? "Reclamado"
              : isClaimableNow
                ? "Pendiente"
                : needsActivity
                  ? "Haz una tarea/test"
                  : isTomorrow
                    ? "Mañana"
                    : locked
                      ? "Bloqueado"
                      : "Pasado";

            // ✅ mantenemos comportamiento: solo clickable el “día actual”
            // pero si falta actividad, lo dejamos clickable para mostrar el toast
            const disabledBtn = claiming || claimed || (!isClaimableNow && !needsActivity);

            return (
              <button
                key={absolute}
                className={`day ${status} ${rewardClass}`}
                data-streak-day={absolute}
                onClick={() => tryClaim(absolute)}
                disabled={disabledBtn}
                title={`Día ${absolute} • ${label}`}
                aria-label={`Día ${absolute} • ${label}`}
              >
                <div className="d-index">Día {absolute}</div>

                <div className={`d-reward ${reward.type === "comodin" ? "reward-comodin" : ""}`} aria-hidden>
                  <span className="r-ic">{reward.icon}</span>
                  <span className="r-tx">{reward.type === "comodin" ? "COMODÍN" : reward.text}</span>
                  {reward.type === "coins" ? <span className="r-pill">{reward.pill}</span> : null}
                </div>

                <div className="d-badge">{label}</div>
              </button>
            );
          })}
        </div>
      </div>

      <GlobalToast
        open={toastOpen}
        title={toastCfg.title}
        subtitle={toastCfg.subtitle}
        icon={toastCfg.icon}
        onDone={() => setToastOpen(false)}
        duration={toastCfg.icon === "🧩" ? 3200 : 6000}
        variant={variant}
      />

      <NextDayTip anchorEl={tip.anchor} open={tip.open} onClose={() => setTip({ open: false, anchor: null })} />

      <style>{`
       .streak-modal-overlay{
  position:fixed; inset:0;
  background: rgba(0,0,0,.4);
  backdrop-filter: blur(4px);
  display:flex; align-items:center; justify-content:center;
  z-index: 5000; padding:14px;
}

.streak-modal{
  width:min(860px, 100%);
  max-height: 92svh;
  overflow:auto;
  background:#fff;
  border-radius:24px;
  box-shadow: 0 30px 80px rgba(0,0,0,.35);
  animation: smPop .2s ease;
}
@keyframes smPop{ from{ transform: scale(.96); opacity:0; } to{ transform: scale(1); opacity:1; } }

.sm-head{
  display:flex; align-items:center; justify-content:space-between;
  padding:14px 16px; border-bottom:1px solid #f1f5f9;
}
.sm-head .title{
  display:flex; align-items:center; gap:8px;
  font-weight:800; color:#1e3a8a; font-size:.95rem;
}
.sm-head .bolt{ font-size:18px; }
.sm-head .close{
  border:none; background:transparent; color:#9ca3af;
  padding:6px; border-radius:999px; cursor:pointer;
  transition: background .15s, color .15s;
  font-size:16px;
}
.sm-head .close:hover{ background:#f1f5f9; color:#374151; }

.sm-sub{
  padding:10px 16px 6px;
  display:flex; align-items:center; gap:10px; flex-wrap:wrap;
  font-size:.78rem; font-weight:700; color:#475569;
}
.sm-sub .dot{ color:#cbd5e1; }
.sm-sub .pager{
  margin-left:auto; display:flex; align-items:center; gap:6px;
  font-size:.78rem; font-weight:700; color:#475569;
  border:1px solid #e5e7eb; border-radius:999px; padding:4px 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
}
.sm-sub .pager button{
  width:20px; height:20px; border-radius:999px;
  border:none; background:transparent; cursor:pointer;
  color:#9ca3af; font-size:14px; display:flex; align-items:center; justify-content:center;
  transition: color .15s;
}
.sm-sub .pager button:hover:not(:disabled){ color:#2563eb; }
.sm-sub .pager button:disabled{ opacity:.3; cursor:not-allowed; }

.need-act-pill{
  font-size:.72rem; padding:3px 10px; border-radius:999px;
  background:#fff7ed; border:1px solid #fed7aa;
  color:#9a3412; font-weight:800;
}

.grid{
  padding:18px 20px 22px;
  display:grid;
  gap:12px;
  grid-template-columns: repeat(5, minmax(0,1fr));
}

@media (max-width:560px){ .grid{ grid-template-columns: repeat(3, minmax(0,1fr)); } }

.day{
  display:flex; flex-direction:column;
  align-items:center; justify-content:space-between;
  height: 106px;
  padding:10px 8px;
  border-radius:14px;
  border:1px solid #f1f5f9;
  background:#fff;
  cursor:pointer;
  transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease;
  gap:4px;
}
.day:hover:not(:disabled){ transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0,0,0,.07); }

.day .d-index{
  font-weight:800; font-size:.7rem; color:#1e3a8a; line-height:1;
}

.day .d-reward{
  display:flex; flex-direction:column; align-items:center; gap:2px;
}
.day .d-reward .r-ic{ font-size:15px; line-height:1; }
.day .d-reward .r-tx{
  font-weight:900; font-size:.82rem; color:#0f172a; line-height:1;
}
.day .d-reward .r-pill{
  font-size:.65rem; padding:2px 6px; border-radius:999px;
  background:#eef2ff; border:1px solid #c7d2fe; color:#3730a3;
}
.day .d-reward.reward-comodin .r-tx{
  font-size:.68rem; letter-spacing:.4px; color:#7c3aed;
}

.day .d-badge{
  font-size:.65rem; font-weight:700;
  width:100%; text-align:center; color:#9ca3af;
  border-radius:6px; padding:2px 0;
}

/* Estados */
.day.claimed{
  border-color:#bbf7d0; background:#f0fdf4;
}
.day.claimed .d-index{ color:#15803d; }
.day.claimed .d-reward .r-tx{ color:#15803d; }
.day.claimed .d-badge{ color:#16a34a; }

.day.pending{
  border-color:#93c5fd; background:#eff6ff;
  transform: scale(1.03);
  box-shadow: 0 4px 14px rgba(37,99,235,.12);
}
.day.pending .d-index{ color:#1d4ed8; }
.day.pending .d-badge{ color:#2563eb; font-weight:900; }
.day.pending:not(.reward-big):not(.reward-max){
  animation: pulsePending 1.4s ease-in-out infinite;
}

.day.need-activity{
  border-color:#e2e8f0; background:#f8fafc;
}
.day.need-activity .d-badge{ color:#ea580c; font-weight:800; }

.day.locked{ opacity:.55; cursor:not-allowed; }

/* Bonus y comodín */
.day.reward-big:not(.claimed){
  border-color:#fde68a; background:#fffbeb;
}
.day.reward-big .d-reward .r-tx{ color:#b45309; }
.day.reward-big .d-reward .r-pill{ background:#fef3c7; border-color:#fbbf24; color:#92400e; }

.day.reward-max:not(.claimed){
  border-color:#ddd6fe; background:#faf5ff;
}
.day.reward-max .d-badge{ color:#7c3aed; }

/* Animaciones */
@keyframes pulsePending{
  0%{ box-shadow: 0 0 0 0 rgba(59,130,246,.25); }
  70%{ box-shadow: 0 0 0 10px rgba(59,130,246,0); }
  100%{ box-shadow: 0 0 0 0 rgba(59,130,246,0); }
}
.day.pending.reward-big{ animation: pulseBig 1.4s ease-in-out infinite; }
@keyframes pulseBig{
  0%{ box-shadow: 0 0 0 0 rgba(245,158,11,.28); }
  70%{ box-shadow: 0 0 0 10px rgba(245,158,11,0); }
  100%{ box-shadow: 0 0 0 0 rgba(245,158,11,0); }
}
.day.reward-max:not(.claimed){ animation: pulseMax 1.4s ease-in-out infinite; }
@keyframes pulseMax{
  0%{ box-shadow: 0 0 0 0 rgba(168,85,247,.22); }
  70%{ box-shadow: 0 0 0 10px rgba(168,85,247,0); }
  100%{ box-shadow: 0 0 0 0 rgba(168,85,247,0); }
}
      `}</style>
    </div>,
    document.body
  );
};

export default StreakModal;
